import { useSyncExternalStore } from "react";
import { store as nativeStore } from "./native/storage";
import { setBearerToken, api } from "./api";
import { getDeviceId, getPlatform } from "./native/device";

export type User = {
  id: string;
  handle: string;
  display_name: string;
  avatar?: string | null;
  created_at: number;
  email?: string | null;
  email_verified?: boolean;
};

export type ChainMineRow = {
  id: string; title: string; alive: number; created_at: number; best_length: number;
  is_private: number; valid_length: number;
  last_from_user: string | null; last_from_handle: string | null;
  last_to_handle: string | null; last_expires_at: number | null; my_turn: boolean;
};

// Minimal shape of a /punch/pairs row — see Chain.tsx's fuller `Pair` type for
// the complete server response.
export type PunchPairRow = {
  mate_handle: string; chain_id: string | null; streak_days: number;
  state: "your_move" | "their_move" | "dying" | "dead"; died_at: number | null;
};

type State = {
  user: User | null;
  token: string | null;
  ready: boolean;
  knownChainIds: string[];
  // shown-once after signup or recovery rotation; cleared once user dismisses
  pendingRecoveryCode: string | null;
  // shown-once after the recovery code is acknowledged on a fresh signup,
  // so we can nudge the user to invite a friend before dropping them into the app.
  pendingInvite: boolean;
  // captured from a /chain/:id?invite=CODE deep link at boot; redeemed once the
  // user is authed (it must survive the onboarding flow, which rewrites the URL).
  pendingChainInvite: { chainId: string; code: string } | null;
  // server capability: is transactional email deliverable? Gates all email UI so
  // we never show a "code sent" prompt that can't actually send.
  emailEnabled: boolean;
  punchChains: ChainMineRow[];
  punchPairs: PunchPairRow[];
  // A capture whose reveal was dismissed while still "pending" (validation not
  // back yet) via the reveal's "run in background" affordance. Tracked here
  // (not the reveal component's local state, which dies on unmount) so an
  // app-level watcher can keep polling and surface the result as a toast even
  // after the user has left Home / switched tabs. In-memory only — surviving
  // an app restart is not required, so this is never persisted to nativeStore.
  pendingCapture: { id: string; photo: string | null } | null;
};

const initial: State = {
  user: null, token: null, ready: false,
  knownChainIds: [],
  pendingRecoveryCode: null, pendingInvite: false,
  pendingChainInvite: null,
  emailEnabled: false,
  punchChains: [],
  punchPairs: [],
  pendingCapture: null,
};

let state: State = initial;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const getSnap = () => state;
export const useStore = <T>(selector: (s: State) => T): T =>
  useSyncExternalStore(subscribe, () => selector(getSnap()), () => selector(getSnap()));
const update = (patch: Partial<State>) => { state = { ...state, ...patch }; emit(); };

const KEY_USER  = "yelo.user.v3";   // bumped: new shape carries id+handle from server
const KEY_TOKEN = "yelo.token.v1";
const KEY_CHAINS = "yelo.chains.v2";
const KEY_PENDING_INVITE = "yelo.pendingChainInvite.v1";

export const hydrate = async () => {
  const [u, t, chains, pendingInv] = await Promise.all([
    nativeStore.get<User>(KEY_USER),
    nativeStore.get<string>(KEY_TOKEN),
    nativeStore.get<string[]>(KEY_CHAINS),
    nativeStore.get<{ chainId: string; code: string }>(KEY_PENDING_INVITE),
  ]);
  if (t) setBearerToken(t);

  // Flip ready immediately with cached state so the UI paints without waiting
  // on a network round-trip. /me runs in the background to refresh + catch 401.
  update({
    user: u ?? null,
    token: t ?? null,
    knownChainIds: chains ?? [],
    pendingChainInvite: pendingInv ?? null,
    ready: true,
  });

  // best-effort capability probe (public, no auth needed)
  api<{ email_enabled: boolean }>("/config")
    .then(cfg => update({ emailEnabled: !!cfg.email_enabled }))
    .catch(() => {});

  if (!t) return;

  // 3s timeout — a wedged network must never keep us logged-in-but-stale forever,
  // but it also must never block first paint. AbortController kills the fetch on time-out.
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 3000);
  try {
    const me = await api<{ user: User }>("/me", { signal: ctrl.signal });
    await nativeStore.set(KEY_USER, me.user);
    update({ user: me.user });
  } catch (e: any) {
    if (e?.status === 401) {
      await nativeStore.del(KEY_TOKEN);
      await nativeStore.del(KEY_USER);
      setBearerToken(null);
      update({ user: null, token: null });
    }
  } finally {
    clearTimeout(timer);
  }
};

export const setAuth = async (user: User, token: string, recoveryCode?: string) => {
  await nativeStore.set(KEY_USER, user);
  await nativeStore.set(KEY_TOKEN, token);
  setBearerToken(token);
  update({
    user, token,
    pendingRecoveryCode: recoveryCode ?? null,
    // any time we hand out a fresh recovery code we're either at signup or post-recover.
    // queue the invite step too so the user passes through it once.
    pendingInvite: recoveryCode ? true : state.pendingInvite,
  });
};

export const clearAuth = async () => {
  await nativeStore.del(KEY_USER);
  await nativeStore.del(KEY_TOKEN);
  setBearerToken(null);
  update({ user: null, token: null });
};

export const dismissRecoveryCode = () => update({ pendingRecoveryCode: null });
export const dismissInvite = () => update({ pendingInvite: false });

// ---- signup + login helpers ----
type AuthResponse = { user: User; token: string; recoveryCode?: string };

export const signup = async (handle: string, displayName?: string): Promise<AuthResponse> => {
  const deviceId = await getDeviceId();
  const r = await api<AuthResponse>("/auth/signup", {
    method: "POST",
    body: JSON.stringify({
      handle, displayName: displayName ?? handle,
      deviceId, createdVia: getPlatform(),
    }),
  });
  await setAuth(r.user, r.token, r.recoveryCode);
  return r;
};

export const loginByDevice = async (): Promise<AuthResponse | null> => {
  const deviceId = await getDeviceId();
  try {
    const r = await api<AuthResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ deviceId }),
    });
    await setAuth(r.user, r.token);
    return r;
  } catch (e: any) {
    if (e?.status === 404) return null;
    throw e;
  }
};

export const recover = async (handle: string, code: string): Promise<AuthResponse> => {
  const deviceId = await getDeviceId();
  const r = await api<AuthResponse>("/auth/recover", {
    method: "POST",
    body: JSON.stringify({ handle, recoveryCode: code, deviceId }),
  });
  await setAuth(r.user, r.token, r.recoveryCode);
  return r;
};

// ---- email anchor (the new primary recovery + contact channel) ----
const KEY_EMAIL_SNOOZE = "yelo.emailSnooze.v1";
export const getEmailSnoozeUntil = async (): Promise<number> =>
  (await nativeStore.get<number>(KEY_EMAIL_SNOOZE)) ?? 0;
export const snoozeEmailPrompt = async (ms: number) => {
  await nativeStore.set(KEY_EMAIL_SNOOZE, Date.now() + ms);
};

// Authenticated: attach + verify an email on the current account.
export const startEmailVerify = async (email: string): Promise<void> => {
  await api("/auth/email/start", { method: "POST", body: JSON.stringify({ email }) });
};
export const confirmEmailCode = async (code: string): Promise<void> => {
  const r = await api<{ email: string; email_verified: boolean }>(
    "/auth/email/verify", { method: "POST", body: JSON.stringify({ code }) },
  );
  if (state.user) {
    const u = { ...state.user, email: r.email, email_verified: true };
    await nativeStore.set(KEY_USER, u);
    update({ user: u });
  }
};

// Unauthenticated: email-based recovery onto a new device.
export const emailRecoverStart = async (email: string): Promise<void> => {
  const deviceId = await getDeviceId();
  await api("/auth/email/recover", { method: "POST", body: JSON.stringify({ email, deviceId }) });
};
export const emailRecoverConfirm = async (email: string, code: string): Promise<void> => {
  const deviceId = await getDeviceId();
  const r = await api<AuthResponse>(
    "/auth/email/recover/confirm", { method: "POST", body: JSON.stringify({ email, code, deviceId }) },
  );
  await setAuth(r.user, r.token);
};

// ---- known chains (unchanged from v1) ----
export const addKnownChain = async (id: string) => {
  if (state.knownChainIds.includes(id)) return;
  const next = [id, ...state.knownChainIds].slice(0, 50);
  await nativeStore.set(KEY_CHAINS, next);
  update({ knownChainIds: next });
};

export const removeKnownChain = async (id: string) => {
  if (!state.knownChainIds.includes(id)) return;
  const next = state.knownChainIds.filter(x => x !== id);
  await nativeStore.set(KEY_CHAINS, next);
  update({ knownChainIds: next });
};

export const setPendingChainInvite = async (chainId: string, code: string) => {
  const next = { chainId, code };
  await nativeStore.set(KEY_PENDING_INVITE, next);
  update({ pendingChainInvite: next });
};

export const clearPendingChainInvite = async () => {
  await nativeStore.del(KEY_PENDING_INVITE);
  update({ pendingChainInvite: null });
};

// Session data, fetched fresh (not persisted). Each fetch swallows its own
// errors so one endpoint failing offline doesn't drop the other's rows.
export const refreshPunchChains = async () => {
  if (!state.user) return;
  const [chains, pairs] = await Promise.all([
    api<ChainMineRow[]>("/chains/mine").then(v => v, () => null),
    api<{ pairs: PunchPairRow[] }>("/punch/pairs").then(v => v, () => null),
  ]);
  update({
    ...(chains ? { punchChains: chains } : {}),
    ...(pairs ? { punchPairs: pairs.pairs } : {}),
  });
};

// See `pendingCapture` on State — set when a reveal is dismissed mid-grade,
// cleared once the app-level watcher has toasted the result (or the same
// capture's reveal is reopened, which supersedes the background poll).
export const setPendingCapture = (id: string, photo: string | null) =>
  update({ pendingCapture: { id, photo } });

export const clearPendingCapture = (id?: string) => {
  if (id && state.pendingCapture?.id !== id) return;
  update({ pendingCapture: null });
};
